"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import TalkToSalesButton from "@/components/sales/TalkToSales";
import { SMS_FOOTNOTE } from "@/lib/planCopy";

interface ApiPlan {
  tier: string;
  name: string;
  description: string;
  monthly_price_cents: number;
  annual_price_cents: number | null;
  features_display: string[];
  is_active: boolean;
}

interface TierDef {
  tier: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number | null;
  description: string;
  cta: string;
  ctaHref: string;
  highlight: boolean;
  features: string[];
}

// Static UI config — not price data
const TIER_UI: Record<string, { cta: string; ctaHref: string; highlight: boolean }> = {
  free: { cta: "Get started free", ctaHref: "/register", highlight: false },
  pro: { cta: "Upgrade to Pro", ctaHref: "/dashboard/billing", highlight: true },
  business: { cta: "Upgrade to Business", ctaHref: "/dashboard/billing", highlight: false },
  // Opens the "Talk to sales" form (a mailto: did nothing for webmail users).
  enterprise: { cta: "Contact sales", ctaHref: "", highlight: false },
};

const TIER_ORDER = ["free", "pro", "business", "enterprise"];
// The cards and the comparison show the PAID plans; Free is one line above them
// (owner, 2026-10-04). The launch promo ended 2026-10-01 and was removed.
const PAID_TIERS = ["pro", "business", "enterprise"];

function fmtUSD(amount: number): string {
  return amount % 1 === 0 ? `$${amount.toFixed(0)}` : `$${amount.toFixed(2)}`;
}

// Standard per-month amount for a tier/period (0 = free, <0 = custom/contact sales)
function monthlyAmount(tier: TierDef, period: "monthly" | "annual"): number {
  if (tier.monthlyPrice <= 0) return tier.monthlyPrice;
  if (period === "annual" && tier.annualPrice) return tier.annualPrice / 12;
  return tier.monthlyPrice;
}

/** "2 months free" — how many months a year of annual billing saves, from the
 *  live prices (never a hardcoded percentage: "Save 20%" was really ~17%). */
function monthsFree(tiers: TierDef[]): number {
  const t = tiers.find((x) => x.tier === "pro" && x.monthlyPrice > 0 && x.annualPrice);
  if (!t || !t.annualPrice) return 0;
  return Math.round(12 - t.annualPrice / t.monthlyPrice);
}

// Monthly envelope allowance per tier (null = custom). Billed yearly, Pro and
// Business get the whole year (× 12) up front — a real pool (usage_service.annual_pool).
const ENVELOPE_CAPS: Record<string, number | null> = { free: 10, pro: 25, business: 100, enterprise: null };
function envCapCell(tier: string, period: "monthly" | "annual"): string {
  const cap = ENVELOPE_CAPS[tier];
  if (cap == null) return "Custom";
  return period === "annual" ? `${(cap * 12).toLocaleString()} / year, use any time` : `${cap} / month`;
}

const faqs = [
  {
    q: "Can I try Pro features before paying?",
    a: "Our Free plan is full-featured to get started. Upgrade to Pro for more recipients, AI documents, and branding removal — or Business for teams, larger bulk sends, and shared templates. Cancel anytime.",
  },
  {
    q: "Do you offer plans for larger teams and enterprises?",
    a: "Yes. Business adds teams, larger bulk sends, and custom domains. Enterprise adds unlimited users, SSO/SAML, an SLA, and volume pricing — contact sales for a quote. The live API, webhooks and MCP server come with every plan, including Free.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit cards, debit cards, and select local payment methods through our secure payment processor.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel your subscription at any time from your billing page. You'll keep access until the end of your billing period.",
  },
  {
    q: "What happens to my documents if I downgrade?",
    a: "Your existing signed documents remain accessible. Templates become read-only, and new envelopes will follow your new plan's limits.",
  },
  {
    q: "Do you offer annual billing?",
    a: "Yes! Annual billing gives you two months free. Toggle between monthly and annual pricing at the top of this page. You can switch billing periods anytime from your dashboard.",
  },
  {
    q: "Is SignForge legally binding?",
    a: "Electronic signatures are recognised in law: the US ESIGN Act and UETA, and the EU's eIDAS Regulation, say a signature can't be denied legal effect solely because it is electronic. Some documents still need ink, and whether a particular document qualifies depends on the transaction and the country. SignForge records who signed, when and from where, with a fingerprint of the document, as evidence.",
  },
  {
    q: "How does this compare to DocuSign?",
    a: "DocuSign has no free plan for sending documents — a 30-day trial, then a paid plan from about $10 a month billed annually for 5 envelopes. Our Free plan sends 10 a month at $0, with no time limit.",
    link: { href: "/is-docusign-free", label: "DocuSign pricing, explained →" },
  },
  {
    q: "What is Bulk Signing, and how is it different from Bulk Send?",
    a: "Bulk Send fans one document out to many recipients (each signs their own copy). Bulk Signing is the reverse: one authorised person executes many documents in a single signing session — instead of signing each one individually. It's built for high-volume, identical-template documents like offer letters or policy acknowledgments, where the same authority signs the same company block on every copy. They're complementary.",
  },
  {
    q: "With Bulk Signing, is the company's mark a real signature or just a stamp?",
    a: "A real, per-document cryptographic mark — not a pasted image. Each document is sealed to its own content hash and is independently verifiable. We never reuse one signature image across documents; that wouldn't be a genuine signature. The authority reviews a representative copy plus the full batch manifest (and can open any individual document on demand), then records one explicit, batch-scoped consent that is referenced from every document's proof. In legal terms the company's mark is an advanced electronic seal — recognised under eIDAS (EU), ESIGN/UETA (US), and India's IT Act, by concept.",
  },
];

/** Free is not a column any more — one line above the paid plans (owner, 2026-10-04). */
function FreePlanLine({ tiers }: { tiers: TierDef[] }) {
  const freeCap = ENVELOPE_CAPS.free;
  if (tiers.length > 0 && !tiers.some((t) => t.tier === "free")) return null;
  return (
    <div className="mb-10 rounded-2xl border border-slate-200 bg-white px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">
          Free plan available &mdash; {freeCap} envelopes / month, no credit card needed.
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          Signed documents are kept with no time limit on every plan. The live API, webhooks and MCP server are included, and bulk send up to 5 recipients per batch.
        </p>
      </div>
      <Link
        href="/register"
        data-testid="free-plan-cta"
        onClick={() => {
          if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
            (window as any).sfAnalytics.track("pricing_cta_clicked", "Billing", "free", undefined, {});
          }
        }}
        className="shrink-0 inline-flex items-center justify-center rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition-colors"
      >
        Start free &rarr;
      </Link>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function formatPrice(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

function getDisplayPrice(tier: TierDef, period: "monthly" | "annual"): string {
  if (tier.monthlyPrice === 0) return "$0";
  if (tier.monthlyPrice < 0) return "Custom";
  if (period === "annual" && tier.annualPrice) {
    const mo = tier.annualPrice / 12;
    return `$${mo % 1 === 0 ? mo.toFixed(0) : mo.toFixed(2)}`;
  }
  return `$${tier.monthlyPrice % 1 === 0 ? tier.monthlyPrice.toFixed(0) : tier.monthlyPrice.toFixed(2)}`;
}

function getPeriodLabel(tier: TierDef): string {
  if (tier.monthlyPrice === 0) return "forever";
  if (tier.monthlyPrice < 0) return "";
  return "/mo";
}

function apiToTierDef(plan: ApiPlan): TierDef {
  const ui = TIER_UI[plan.tier] || { cta: plan.name, ctaHref: "/dashboard/billing", highlight: false };
  const isEnterprise = plan.tier === "enterprise";
  return {
    tier: plan.tier,
    name: plan.name,
    monthlyPrice: isEnterprise ? -1 : plan.monthly_price_cents / 100,
    annualPrice: plan.annual_price_cents ? plan.annual_price_cents / 100 : null,
    description: plan.description,
    cta: ui.cta,
    ctaHref: ui.ctaHref,
    highlight: ui.highlight,
    features: plan.features_display,
  };
}

export default function PricingContent() {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("annual");
  const [tiers, setTiers] = useState<TierDef[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "/api";
    fetch(`${apiUrl}/billing/plans`)
      .then((res) => res.json())
      .then((plans: ApiPlan[]) => {
        const sorted = plans.sort(
          (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier)
        );
        setTiers(sorted.map(apiToTierDef));
      })
      .catch(() => {
        // Silence — empty state will show
      })
      .finally(() => setLoading(false));
  }, []);

  // Build comparison table price row dynamically
  function priceCell(tierName: string): string {
    const t = tiers.find((t) => t.tier === tierName);
    if (!t) return "—";
    if (t.monthlyPrice === 0) return "Free";
    if (t.monthlyPrice < 0) return "Custom";
    return `${fmtUSD(monthlyAmount(t, billingPeriod))}/mo`;
  }

  return (
    <>
      {/* Hero */}
      <section className="py-20 md:py-28 text-center px-4">
        <h1 className="font-display text-4xl md:text-5xl font-bold text-ink tracking-tight">
          Pricing that scales with you
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
          From your first signature to enterprise volume. Every plan includes open-standards verification and a full audit trail — start free, no credit card, cancel anytime.
        </p>

        {/* Billing Period Toggle */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <span className={`text-sm font-medium ${billingPeriod === "monthly" ? "text-ink" : "text-slate-400"}`}>
            Monthly
          </span>
          <button
            onClick={() => {
              const next = billingPeriod === "monthly" ? "annual" : "monthly";
              setBillingPeriod(next);
              if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
                (window as any).sfAnalytics.track("billing_period_toggled", "Billing", next, undefined, { source: "pricing" });
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              billingPeriod === "annual" ? "bg-brand-600" : "bg-slate-200"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm ${
                billingPeriod === "annual" ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingPeriod === "annual" ? "text-ink" : "text-slate-400"}`}>
            Annual
          </span>
          {monthsFree(tiers) > 0 && (
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
              {monthsFree(tiers)} months free
            </span>
          )}
        </div>
      </section>

      {/* Pricing Cards — paid plans; Free is the line above them */}
      <section className="max-w-5xl mx-auto px-4 pb-20">
        <FreePlanLine tiers={tiers} />
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 animate-pulse">
                <div className="h-5 w-20 bg-slate-200 rounded mb-2" />
                <div className="h-4 w-40 bg-slate-100 rounded mb-4" />
                <div className="h-10 w-24 bg-slate-200 rounded mb-6" />
                <div className="space-y-2">
                  {[0, 1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-4 bg-slate-100 rounded" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.filter((t) => PAID_TIERS.includes(t.tier)).map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl border p-6 flex flex-col ${
                  tier.highlight
                    ? "border-brand-300 bg-white shadow-lg shadow-brand-100/30 ring-1 ring-brand-200"
                    : "border-slate-200 bg-white"
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-600 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Most popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="font-display text-lg font-bold text-ink">{tier.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                  <div className="mt-4">
                      <div className="flex items-baseline gap-1">
                        <span className="font-display text-4xl font-bold text-ink">
                          {getDisplayPrice(tier, billingPeriod)}
                        </span>
                        {getPeriodLabel(tier) && (
                          <span className="text-sm text-slate-500">{getPeriodLabel(tier)}</span>
                        )}
                      </div>
                      {billingPeriod === "annual" && tier.annualPrice && (
                        <p className="text-xs text-slate-400 mt-1">
                          ${tier.annualPrice.toFixed(2)}/year &middot; billed annually
                        </p>
                      )}
                  </div>
                </div>

                <ul className="space-y-2.5 flex-1 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckIcon />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {tier.tier === "enterprise" ? (
                  <TalkToSalesButton
                    source="pricing"
                    testId="pricing-contact-sales"
                    className="block w-full text-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-all bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    {tier.cta}
                  </TalkToSalesButton>
                ) : (
                <Link
                  href={tier.ctaHref}
                  onClick={() => {
                    if (typeof window !== "undefined" && (window as any).sfAnalytics?.track) {
                      (window as any).sfAnalytics.track("pricing_cta_clicked", "Billing", tier.tier, undefined, { billing_period: billingPeriod });
                    }
                  }}
                  className={`block text-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                    tier.highlight
                      ? "bg-brand-600 text-white hover:bg-brand-700 shadow-sm"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {tier.cta}
                </Link>
                )}
              </div>
            ))}
          </div>
        )}
        {!loading && tiers.length > 0 && (
          <p className="mt-6 text-xs text-slate-400 text-center">
            {SMS_FOOTNOTE}
          </p>
        )}
      </section>

      {/* Comparison Table (desktop) */}
      {!loading && tiers.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 pb-20 hidden lg:block">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">Compare plans</h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-medium text-slate-500">Feature</th>
                  <th className="text-center py-3 px-4 font-semibold text-brand-700 bg-brand-50/40">Pro</th>
                  <th className="text-center py-3 px-4 font-semibold text-ink">Business</th>
                  <th className="text-center py-3 px-4 font-semibold text-ink">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {([
                  { section: "Signing", rows: [
                    ["Monthly price", priceCell("pro"), priceCell("business"), priceCell("enterprise")],
                    ["Envelope sends", envCapCell("pro", billingPeriod), envCapCell("business", billingPeriod), envCapCell("enterprise", billingPeriod)],
                    ["Recipients / envelope", "5", "Unlimited", "Unlimited"],
                    ["Field types", "All 12", "All 12", "All 12"],
                    ["Decline to sign", "\u2713", "\u2713", "\u2713"],
                    ["Download archive (ZIP)", "\u2713", "\u2713", "\u2713"],
                  ]},
                  { section: "AI & Documents", rows: [
                    ["Amanda AI assistant (42 tools)", "\u2713", "\u2713", "\u2713"],
                    ["AI document generation", "25/mo", "Unlimited", "Unlimited"],
                    ["AI document summaries", "50/mo", "Unlimited", "Unlimited"],
                    ["AI field detection", "\u2713", "\u2713", "\u2713"],
                  ]},
                  { section: "Security & Verification", rows: [
                    ["Open-standards verification (W3C VC)", "\u2713", "\u2713", "\u2713"],
                    ["Public transparency log", "\u2713", "\u2713", "\u2713"],
                    ["Offline verification (embedded keys)", "\u2713", "\u2713", "\u2713"],
                    ["QR code verification", "\u2713", "\u2713", "\u2713"],
                    ["Audit trail & certificate", "\u2713", "\u2713", "\u2713"],
                  ]},
                  { section: "Workflow & Automation", rows: [
                    ["Default templates", "46", "50", "50"],
                    ["Custom templates", "Unlimited", "Unlimited", "Unlimited"],
                    ["Scheduled sending", "\u2713", "\u2713", "\u2713"],
                    ["Post-sign redirect", "\u2713", "\u2713", "\u2713"],
                    ["Pre-fill / merge fields", "\u2713", "\u2713", "\u2713"],
                    ["Email reminders", "\u2713", "\u2713", "\u2713"],
                    ["Form responses (CSV export)", "---", "\u2713", "\u2713"],
                  ]},
                  { section: "Delivery", rows: [
                    ["Shareable signing links / mo", "50", "Unlimited", "Unlimited"],
                    ["CC recipients", "\u2713", "\u2713", "\u2713"],
                    ["SMS / WhatsApp delivery*", "\u2713", "\u2713", "\u2713"],
                  ]},
                  { section: "Integration & API", rows: [
                    ["Custom metadata (API)", "---", "\u2713", "\u2713"],
                    ["API access & webhooks", "\u2713", "\u2713", "\u2713"],
                    ["MCP server (18 tools)", "\u2713", "\u2713", "\u2713"],
                    ["SSO / SAML", "---", "---", "\u2713"],
                  ]},
                  { section: "Platform", rows: [
                    ["PDF tools", "12 free", "Unlimited", "Unlimited"],
                    ["Tool uses / day", "50", "Unlimited", "Unlimited"],
                    ["Locker storage", "500 MB", "Unlimited", "Unlimited"],
                    ["Branding removal", "\u2713", "\u2713", "\u2713"],
                    ["Bulk send (recipients / batch)", "50", "200", "500"],
                    ["Teams", "---", "\u2713", "\u2713"],
                    ["Custom domain", "---", "\u2713", "\u2713"],
                  ]},
                ] as { section: string; rows: string[][] }[]).map((group) => (
                  <React.Fragment key={group.section}>
                    <tr>
                      <td colSpan={4} className="bg-slate-50/80 px-4 py-2 border-b border-t border-slate-100">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{group.section}</span>
                      </td>
                    </tr>
                    {group.rows.map(([feature, ...values]) => (
                      <tr key={feature} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-2.5 px-4 text-slate-700">{feature}</td>
                        {values.map((v, i) => (
                          <td key={i} className={`py-2.5 px-4 text-center text-slate-600 ${i === 0 ? "bg-brand-50/20" : ""}`}>
                            {v === "---" ? (
                              <span className="text-slate-300">&mdash;</span>
                            ) : v === "\u2713" ? (
                              <svg className="w-5 h-5 text-emerald-500 mx-auto" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                            ) : (
                              v
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-400 text-center">
            Monthly plans reset each month. Yearly plans get the full year&apos;s envelopes up front, to use any time in the
            subscription year. Signed documents are kept with no time limit on every plan.
          </p>
          <p className="mt-2 text-xs text-slate-400 text-center">
            {SMS_FOOTNOTE}
          </p>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 pb-24">
        <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">Frequently asked questions</h2>
        <div className="space-y-4">
          {faqs.map(({ q, a, link }) => (
            <details key={q} className="group border border-slate-200 rounded-xl bg-white">
              <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-ink list-none flex items-center justify-between">
                {q}
                <svg className="h-4 w-4 text-slate-400 shrink-0 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </summary>
              <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">
                {a}
                {link && (
                  <>
                    {" "}
                    <Link href={link.href} className="font-medium text-brand-600 hover:text-brand-700">{link.label}</Link>
                  </>
                )}
              </div>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}
